import React, { type FC, type ReactNode, useEffect, useRef, useCallback } from 'react';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { useExperiments } from '@johnlewispartnership/wtr-website-decisioner-client/express';

import ExperimentsContext, { type ExperimentView } from './TrackedExperimentsContext';
import contentSquareReport from './ContentSquareReport';

const MONETATE_DEFAULT_VARIANT = 'default';

const TrackedExperimentsProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { trackEvent } = useAnalytics();
  const {
    experimentsContext: { experiments },
  } = useExperiments();

  const toTrack = useRef(new Map());
  const tracked = useRef(new Map());

  const isExperimentLive = (id: string): boolean => Object.keys(experiments ?? {}).includes(id);

  const trackExperimentViewEvent = ({ id, variant, trackAll }: ExperimentView): void => {
    if (!isExperimentLive(id)) {
      // Mark it as tracked just to save re-checking if it's live again each time
      tracked.current.set(id, 1);

      return;
    }

    if (trackAll || !tracked.current.has(id)) {
      trackEvent({
        event: 'monetate_fe_experiment_viewed',
        monetate_fe_experiment: {
          id,
          variant,
        },
      });
      tracked.current.set(id, 1);
    }
  };

  const trackExperimentPageLoad = useCallback(
    (currentExperiments: typeof experiments): void => {
      const formattedDecisions: { id: string; variant: string }[] = [];
      const formattedLabels: { [name: string]: string } = {};

      Object.entries(currentExperiments ?? {}).forEach(([key, { variant }]) => {
        formattedDecisions.push({ id: `fe_${key}`, variant });
        formattedLabels[`monetateExperiment_${key}`] = variant;
      });

      if (Object.keys(formattedLabels).length > 0) {
        window.apm?.addLabels(formattedLabels);
      }

      trackEvent({
        event: 'monetate_fe_experiment_page_load',
        monetate_fe_experiment_page_load: formattedDecisions,
      });
    },
    [trackEvent],
  );

  const trackImpressions = useCallback((currentExperiments: typeof experiments): void => {
    contentSquareReport(currentExperiments);
  }, []);

  const getExperimentById = (id: string, { manualTracking = false } = {}) => {
    const exp = experiments?.[id];
    const variant = exp?.variant ?? MONETATE_DEFAULT_VARIANT;

    if (!manualTracking) {
      toTrack.current.set(id, variant);
    }

    return {
      variant,
      payload: exp?.payload,
      experienceLabel: exp?.experienceLabel,
      variantLabel: exp?.variantLabel,
      trackExperimentView: (trackAll = false) => {
        // Manual tracking can be useful e.g. for content below the fold which
        // we don't want to deem as having been viewed automatically on page load
        if (manualTracking) {
          trackExperimentViewEvent({ id, variant, trackAll });
        }
      },
    };
  };

  const contextValue = {
    getExperimentById,
    trackExperimentViewEvent,
  };

  useEffect(() => {
    toTrack.current.forEach((variant, id) => {
      if (!tracked.current.has(id)) {
        trackExperimentViewEvent({ id, variant });
      }
    });
  });

  useEffect(() => {
    trackExperimentPageLoad(experiments);
    trackImpressions(experiments);
  }, [experiments, trackExperimentPageLoad, trackImpressions]);

  return <ExperimentsContext.Provider value={contextValue}>{children}</ExperimentsContext.Provider>;
};

export default TrackedExperimentsProvider;
